// Esta página é de usuários para funcionários vá para apps/web/src/pages/admin/hr

import ConfirmModal from '@/components/confirm-modal'
import { ImportDataModal } from '@/components/import/ImportDataModal'
import PaginationControls from '@/components/pagination/PaginationControls'
import { User } from '@/lib/api/common/interfaces/user.interface'
import { ShareableModule } from '@/lib/api/share/share-api'
import {
    activateUserAccount,
    deactivateUserAccount,
    deleteUser,
    getUsers,
    getUsersByCompany,
} from '@/lib/api/users/users-api'
import { useDebounce } from '@/lib/hooks/use-debounce'
import { usePaginatedPage } from '@/lib/hooks/use-paginated-page'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useUrlPage } from '@/lib/hooks/use-url-page'
import { useAuth } from '@/store/hooks'
import { useCallback, useMemo, useState } from 'react'
import {
    MdAdd,
    MdArrowForward,
    MdBlock,
    MdClose,
    MdDeleteForever,
    MdEdit,
    MdEmail,
    MdFileDownload,
    MdMoreVert,
    MdPeople,
    MdPerson,
    MdPhone,
    MdSearch,
} from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

export default function EmployeesListPage() {
    const { user, isMaster } = useAuth()
    const navigate = useNavigate()
    const { canCreate, canUpdate } = useResourcePermissions(
        'security',
        'employees'
    )
    const [searchTerm, setSearchTerm] = useState('')
    const [employeeToDeactivate, setEmployeeToDeactivate] =
        useState<User | null>(null)
    const [employeeToHardDelete, setEmployeeToHardDelete] =
        useState<User | null>(null)
    const [processing, setProcessing] = useState(false)
    const [showImportModal, setShowImportModal] = useState(false)
    const [urlPage, setUrlPage] = useUrlPage()

    const debouncedSearch = useDebounce(searchTerm, 300)

    const filters = useMemo(
        () => ({
            mediatorStatus: 'ACTIVE',
            // Profissional com acesso liberado é usuário do sistema como qualquer
            // outro — some daqui e o operador não entende por que o login existe
            // mas ninguém aparece na lista. Fora ficam só CLIENT e PARTNER, que
            // acessam portais próprios e têm telas de cadastro próprias. O usuário de
            // IA também entra: tem login, perfil e carteira como qualquer vendedora.
            userTypes: 'COMPANY,EMPLOYEE,CUSTOM,PROFESSIONAL,AI',
            search: debouncedSearch.trim() || undefined,
        }),
        [debouncedSearch]
    )

    const fetchFn = useCallback(
        (params: typeof filters & { page: number; limit: number }) => {
            return getUsers(params) as any
        },
        []
    )

    const {
        items: displayedEmployees,
        loading,
        page,
        limit,
        total,
        setPage,
        reload,
    } = usePaginatedPage<User, typeof filters>({
        fetchFn,
        filters,
        limit: 15,
        enabled: !!user?.companyId,
        page: urlPage,
        onPageChange: setUrlPage,
    })

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault()
    }

    const handleActivate = async (employee: User) => {
        try {
            const response = await activateUserAccount(employee.id)
            if (response.success) {
                toast.success('Usuário ativado com sucesso')
                await reload()
            } else {
                toast.error(response.error || 'Erro ao ativar usuário')
            }
        } catch (error) {
            toast.error('Erro ao ativar usuário')
        }
    }

    const handleConfirmDeactivate = async () => {
        if (!employeeToDeactivate) return
        setProcessing(true)
        try {
            const response = await deactivateUserAccount(
                employeeToDeactivate.id
            )
            if (response.success) {
                toast.success('Usuário desativado com sucesso')
                setEmployeeToDeactivate(null)
                await reload()
            } else {
                toast.error(response.error || 'Erro ao desativar usuário')
            }
        } catch (error) {
            toast.error('Erro ao desativar usuário')
        } finally {
            setProcessing(false)
        }
    }

    const handleConfirmHardDelete = async () => {
        if (!employeeToHardDelete) return
        setProcessing(true)
        try {
            const response = await deleteUser(employeeToHardDelete.id)
            if (response.success) {
                toast.success('Usuário excluído definitivamente')
                setEmployeeToHardDelete(null)
                await reload()
            } else {
                toast.error(response.error || 'Erro ao excluir usuário')
            }
        } catch (error) {
            toast.error('Erro ao excluir usuário')
        } finally {
            setProcessing(false)
        }
    }

    return (
        <div className="flex flex-col gap-6 animate-fade-in">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-neutral to-neutral/70 flex items-center justify-center shadow-lg">
                        <MdPeople className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-base-content">
                            Usuários do Sistema
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {total} {total === 1 ? 'usuário' : 'usuários'}{' '}
                            cadastrados
                        </p>
                    </div>
                </div>
                {canCreate && (
                    <div className="flex items-center gap-2">
                        <button
                            className="btn btn-outline btn-secondary gap-2"
                            onClick={() => setShowImportModal(true)}
                        >
                            <MdFileDownload className="text-xl" />
                            <span className="hidden sm:inline">Importar</span>
                        </button>
                        <button
                            className="btn btn-secondary gap-2"
                            onClick={() => navigate('/security/users/new')}
                        >
                            <MdAdd className="text-xl" />
                            <span className="hidden sm:inline">Adicionar</span>
                        </button>
                    </div>
                )}
            </div>

            <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-4">
                <form
                    onSubmit={handleSearch}
                    className="flex items-center gap-2"
                >
                    <div className="flex-1 relative">
                        <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/70 text-xl pointer-events-none z-10" />
                        <input
                            type="text"
                            placeholder="Buscar por nome, email ou telefone..."
                            className="input input-bordered w-full pl-10"
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                        />
                    </div>
                    {searchTerm && (
                        <button
                            type="button"
                            onClick={() => setSearchTerm('')}
                            className="btn btn-ghost btn-sm"
                        >
                            <MdClose className="text-lg" />
                        </button>
                    )}
                </form>
            </div>

            {loading && displayedEmployees.length === 0 ? (
                <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 animate-fade-in">
                    <div className="loading loading-spinner loading-lg text-secondary"></div>
                    <p className="text-base-content/60 text-sm">
                        Carregando usuários...
                    </p>
                </div>
            ) : displayedEmployees.length === 0 && !loading ? (
                <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 bg-base-100 rounded-xl border border-base-300/50 p-8">
                    <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                        <MdPeople className="text-4xl text-base-content/30" />
                    </div>
                    <div className="text-center">
                        <h3 className="text-lg font-semibold text-base-content mb-1">
                            {searchTerm
                                ? 'Nenhum usuário encontrado'
                                : 'Nenhum usuário cadastrado'}
                        </h3>
                        <p className="text-sm text-base-content/60 mb-4">
                            {searchTerm
                                ? 'Tente ajustar sua busca'
                                : 'Comece adicionando usuários para que eles apareçam aqui.'}
                        </p>
                        {!searchTerm && canCreate && (
                            <button
                                onClick={() => navigate('/security/users/new')}
                                className="btn btn-secondary gap-2"
                            >
                                <MdAdd className="text-xl" />
                                Adicionar primeiro usuário
                            </button>
                        )}
                    </div>
                </div>
            ) : (
                <div className="flex flex-col gap-4">
                    {displayedEmployees.map(employee => (
                        <div
                            key={employee.id}
                            className="group bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                        >
                            <div className="flex items-start gap-4 p-4">
                                <div className="relative w-16 h-16 flex-shrink-0 bg-gradient-to-br from-neutral/20 to-neutral/10 rounded-xl flex items-center justify-center border border-neutral/20">
                                    <MdPerson className="text-3xl text-neutral" />
                                </div>

                                <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between gap-2 mb-2">
                                        <h3 className="text-base font-bold text-base-content line-clamp-1 group-hover:text-secondary transition-colors">
                                            {employee.name}
                                        </h3>
                                        {canUpdate && (
                                            <div className="flex gap-2 flex-shrink-0 items-center">
                                                <button
                                                    onClick={() =>
                                                        navigate(
                                                            `/security/users/edit/${employee.id}`
                                                        )
                                                    }
                                                    className="btn btn-circle btn-sm btn-ghost"
                                                    title="Editar usuário"
                                                >
                                                    <MdEdit className="text-lg" />
                                                </button>
                                                <div className="dropdown dropdown-end">
                                                    <label
                                                        tabIndex={0}
                                                        className="btn btn-circle btn-sm btn-ghost"
                                                        title="Mais opções"
                                                    >
                                                        <MdMoreVert className="text-lg" />
                                                    </label>
                                                    <ul
                                                        tabIndex={0}
                                                        className="dropdown-content menu bg-base-100 rounded-box shadow-lg border border-base-300 z-[50] p-2 w-56"
                                                    >
                                                        {employee.active ? (
                                                            <li>
                                                                <button
                                                                    onClick={() =>
                                                                        setEmployeeToDeactivate(
                                                                            employee
                                                                        )
                                                                    }
                                                                    className="text-warning"
                                                                >
                                                                    <MdBlock className="text-lg" />
                                                                    Desativar
                                                                </button>
                                                            </li>
                                                        ) : (
                                                            <li>
                                                                <button
                                                                    onClick={() =>
                                                                        handleActivate(
                                                                            employee
                                                                        )
                                                                    }
                                                                    className="text-success"
                                                                >
                                                                    <MdPerson className="text-lg" />
                                                                    Ativar
                                                                </button>
                                                            </li>
                                                        )}
                                                        <li>
                                                            <button
                                                                onClick={() =>
                                                                    setEmployeeToHardDelete(
                                                                        employee
                                                                    )
                                                                }
                                                                className="text-error"
                                                            >
                                                                <MdDeleteForever className="text-lg" />
                                                                Excluir
                                                                definitivo
                                                            </button>
                                                        </li>
                                                    </ul>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="space-y-2 mb-3">
                                        <div className="flex items-center gap-2 text-sm">
                                            <MdEmail className="text-base-content/50" />
                                            <span className="text-base-content/80">
                                                {employee.email}
                                            </span>
                                        </div>
                                        {employee.phone && (
                                            <div className="flex items-center gap-2 text-sm">
                                                <MdPhone className="text-base-content/50" />
                                                <a
                                                    href={`tel:${employee.phone}`}
                                                    className="text-secondary hover:underline"
                                                >
                                                    {employee.phone}
                                                </a>
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex items-center justify-between pt-3 border-t border-base-300/50">
                                        <div className="flex items-center gap-2">
                                            <span
                                                className={`badge ${
                                                    employee.active
                                                        ? 'badge-success'
                                                        : 'badge-error'
                                                } text-white`}
                                            >
                                                {employee.active
                                                    ? 'Ativo'
                                                    : 'Inativo'}
                                            </span>
                                            <span className="badge badge-outline">
                                                {employee.userType === 'COMPANY'
                                                    ? 'Empresa'
                                                    : employee.userType ===
                                                        'CUSTOM'
                                                      ? 'Personalizado'
                                                      : employee.userType ===
                                                          'PROFESSIONAL'
                                                        ? 'Profissional'
                                                        : employee.userType === 'AI'
                                                          ? 'IA'
                                                          : 'Usuário'}
                                            </span>
                                        </div>

                                        <MdArrowForward className="text-xl text-base-content/30 group-hover:text-secondary group-hover:translate-x-1 transition-all" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                    {total > limit && (
                        <PaginationControls
                            page={page}
                            limit={limit}
                            total={total}
                            loading={loading}
                            onPageChange={setPage}
                            label="usuário"
                        />
                    )}
                </div>
            )}

            <ImportDataModal
                isOpen={showImportModal}
                onClose={() => setShowImportModal(false)}
                module={ShareableModule.EMPLOYEE}
                moduleLabel="Usuários do Sistema"
                fetchItems={async (companyId: number) => {
                    const response = await getUsersByCompany(companyId)
                    return response.success ? response.result : []
                }}
                renderItem={(item: User) => (
                    <div>
                        <p className="text-sm font-medium truncate">
                            {item.name}
                        </p>
                        <p className="text-xs text-base-content/60 truncate">
                            {[item.email, item.phone]
                                .filter(Boolean)
                                .join(' - ') || 'Sem contato'}
                        </p>
                    </div>
                )}
                getItemId={(item: User) => item.id}
                getItemName={(item: User) => item.name}
                onImportComplete={reload}
            />

            <ConfirmModal
                isOpen={!!employeeToDeactivate}
                onClose={() => setEmployeeToDeactivate(null)}
                onConfirm={handleConfirmDeactivate}
                title="Desativar usuário"
                message={`Tem certeza de que deseja desativar "${employeeToDeactivate?.name}"? Ele não poderá mais acessar o sistema, mas os dados serão preservados e podem ser reativados depois.`}
                loading={processing}
                confirmButtonText="Desativar"
                confirmButtonClass="btn-warning"
            />

            <ConfirmModal
                isOpen={!!employeeToHardDelete}
                onClose={() => setEmployeeToHardDelete(null)}
                onConfirm={handleConfirmHardDelete}
                title="Excluir usuário definitivamente"
                message={`ATENÇÃO: esta ação é irreversível. O usuário "${employeeToHardDelete?.name}" e seus dados associados serão removidos permanentemente do sistema. Deseja continuar?`}
                loading={processing}
                confirmButtonText="Excluir definitivamente"
                confirmButtonClass="btn-error"
            />
        </div>
    )
}
